import React, { useState } from "react";
import { ShieldCheck, X, CheckCircle, Circle, LockKey, EnvelopeSimple, Phone, Camera, IdentificationCard, GraduationCap, Briefcase } from "@phosphor-icons/react";

/**
 * Public "Verified Profile" badge shown on profile cards and detail pages.
 * Click to see a privacy-respecting breakdown of what has been verified.
 * NEVER exposes government documents or personal verification files.
 */
const LABELS = {
  email: { title: "Email address", Icon: EnvelopeSimple, mandatory: true },
  phone: { title: "Mobile number", Icon: Phone, mandatory: true },
  photo: { title: "Primary photograph", Icon: Camera, mandatory: true },
  identity: { title: "Government identity", Icon: IdentificationCard, mandatory: true },
  education: { title: "Education", Icon: GraduationCap, mandatory: false },
  employment: { title: "Employment", Icon: Briefcase, mandatory: false },
};

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch (_) { return null; }
}

export function VerifiedBadge({ verifications, size = "md", className = "" }) {
  const [open, setOpen] = useState(false);
  if (!verifications?.badge) return null;

  const px = size === "sm" ? "px-2 py-0.5 text-[9px]" : "px-2.5 py-1 text-[10px]";
  const icon = size === "sm" ? 11 : 12;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        title="Verified Profile — click to see what was verified"
        data-testid="verified-profile-badge"
        className={`inline-flex items-center gap-1 uppercase tracking-widest bg-brand-terracotta text-white rounded-full ${px} ${className} hover:bg-brand-terracotta/90 transition-colors font-semibold`}
      >
        <ShieldCheck size={icon} weight="fill" /> Verified Profile
      </button>
      {open && <VerificationModal verifications={verifications} onClose={() => setOpen(false)} />}
    </>
  );
}

function VerificationModal({ verifications, onClose }) {
  const mandatoryKeys = ["email", "phone", "photo", "identity"];
  const optionalKeys = ["education", "employment"];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="verified-badge-modal"
    >
      <div
        className="bg-white w-full md:max-w-lg md:m-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-brand-line flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={22} weight="fill" className="text-brand-terracotta" />
              <div className="label-eyebrow text-brand-terracotta">Verified Profile</div>
            </div>
            <h2 className="font-serif text-2xl mt-2 text-brand-ink leading-tight">
              What has been verified.
            </h2>
            <p className="mt-2 text-sm text-brand-muted">
              A concierge review confirms the four essentials below. Sensitive documents are never displayed publicly.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="verified-modal-close"
            className="text-brand-muted hover:text-brand-ink flex-shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Mandatory */}
        <div className="p-6">
          <div className="label-eyebrow text-brand-muted mb-3">Mandatory · required for the badge</div>
          <div className="space-y-2">
            {mandatoryKeys.map((k) => (
              <ModalRow key={k} k={k} slot={verifications?.[k]} />
            ))}
          </div>

          {optionalKeys.some((k) => verifications?.[k]?.verified) && (
            <>
              <div className="label-eyebrow text-brand-muted mt-6 mb-3">Optional · self-attested</div>
              <div className="space-y-2">
                {optionalKeys.map((k) => (
                  <ModalRow key={k} k={k} slot={verifications?.[k]} />
                ))}
              </div>
            </>
          )}

          <div className="mt-6 pt-5 border-t border-brand-line flex items-start gap-3 text-xs text-brand-muted">
            <LockKey size={16} weight="duotone" className="text-brand-terracotta flex-shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              Government identity documents are stored privately in an encrypted namespace and never shown to other members.
              Verifications are automatically re-checked every 18 months.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalRow({ k, slot }) {
  const info = LABELS[k];
  if (!info) return null;
  const done = slot?.verified;
  const at = fmtDate(slot?.verified_at);
  const Icon = info.Icon;
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 border ${done ? "border-brand-terracotta/40 bg-brand-terracotta/5" : "border-brand-line bg-brand-bone"}`}
      data-testid={`modal-verify-row-${k}`}
    >
      <Icon size={18} weight="duotone" className={done ? "text-brand-terracotta" : "text-brand-muted"} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-brand-ink font-medium">{info.title}</div>
        {at ? (
          <div className="text-[11px] uppercase tracking-widest text-brand-muted mt-0.5">Verified · {at}</div>
        ) : (
          <div className="text-[11px] uppercase tracking-widest text-brand-muted mt-0.5">Not verified</div>
        )}
      </div>
      {done ? (
        <CheckCircle size={20} weight="fill" className="text-brand-terracotta flex-shrink-0" />
      ) : (
        <Circle size={20} weight="regular" className="text-brand-line flex-shrink-0" />
      )}
    </div>
  );
}

export default VerifiedBadge;
